import React, { useEffect, useState, useContext } from 'react';
import { useMsal } from '@azure/msal-react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
  Alert,
} from '@mui/material';
import CircularProgress from '@mui/material/CircularProgress';

import { UserContext } from '../../contexts/UserContext';
import { getAuthConfig, getApiBaseUrl } from '../../services/configService';

import './JournalEntryModal.css';

const JournalEditModal = ({
  open,
  onClose,
  journal,
  onSuccess,
}) => {
  const { user } = useContext(UserContext);
  const { instance, accounts } = useMsal();
  const authConfig = getAuthConfig();

  const [formData, setFormData] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const getApiAccessToken = async () => {
    if (!accounts.length) return null;

    try {
      const res = await instance.acquireTokenSilent({
        account: accounts[0],
        scopes: [`api://${authConfig.clientId}/access_as_user`],
      });
      return res.accessToken;
    } catch {
      const res = await instance.acquireTokenPopup({
        scopes: [`api://${authConfig.clientId}/access_as_user`],
      });
      return res.accessToken;
    }
  };

  useEffect(() => {
    if (open && journal) {
      setFormData(journal.formData);
      setError(null);
    }
  }, [open, journal]);

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    setError(null);

    try {
      const token = await getApiAccessToken();
      if (!token) throw new Error('Unable to acquire API access token');

      if (!formData.id) {
        throw new Error('Missing journal ID for edit');
      }

      const patchPayload = {
        expected_charge: Number(formData.expected_charge),
        adjusted_charge: Number(formData.adjusted_charge),
        reason: formData.reason,
        note: formData.note,
        staff_id: (formData.staff_id ?? '').toUpperCase(),
        is_escalated: formData.is_escalated,
        is_pricing: formData.is_pricing,
        is_no_error: formData.is_no_error,
        is_local_leadership: formData.is_local_leadership,
      };

      const res = await fetch(
        `${getApiBaseUrl()}/journal/${formData.id}`,
        {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify(patchPayload),
        }
      );

      if (!res.ok) {
        throw new Error(await res.text());
      }
  
      onSuccess?.({
        ...formData,
      });
      onClose();
    } catch (e) {
      setError(e.message);
    } finally {
      setSubmitting(false);
    }
  };

  if (!open || !journal) return null;

  const { displayedFields, readonlyFields } = journal;
  const show = f => displayedFields.has(f);
  const isReadOnly = f => readonlyFields.has(f);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth className="journal-modal">
      <DialogTitle className="journal-modal-header">
        <span className="journal-modal-title">
          Edit Journal Entry
        </span>
      </DialogTitle>

      <DialogContent className="journal-modal-content">
        {error && <Alert severity="error">{error}</Alert>}

        <div className="journal-section">
          <div className="journal-grid">

            {show('expected_charge') && (
              <TextField
                label="Expected Charge"
                size="small"
                type="number"
                value={formData.expected_charge || ''}
                onChange={e => handleChange('expected_charge', e.target.value)}
                InputProps={{ readOnly: isReadOnly('expected_charge') }}
              />
            )}

            {show('adjusted_charge') && (
              <TextField
                label="Recovered Amount"
                size="small"
                type="number"
                value={formData.adjusted_charge || ''}
                onChange={e => handleChange('adjusted_charge', e.target.value)}
              />
            )}

            {show('staff_id') && (
              <TextField
                label="Staff ID"
                size="small"
                value={formData.staff_id || ''}
                onChange={e => handleChange('staff_id', e.target.value)}
              />
            )}

            {show('reason') && (
              <FormControl size="small" className="journal-field">
                <InputLabel id="reason-label">Reason</InputLabel>
                <Select
                  labelId="reason-label"
                  label="Reason"
                  value={formData.reason || ''}
                  onChange={e => handleChange('reason', e.target.value)}
                >
                  {[
                    'Caught in Realtime',
                    'Changed by Doctor',
                    "Code Doesn't Exist",
                    'Estimate Issue',
                    'Hospital Nuance',
                    'Overcharge',
                    'PVL Mismatch',
                    'Workflow Error',
                    'Other',
                  ].map(r => (
                    <MenuItem key={r} value={r}>{r}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}

          </div>

          {show('note') && (
            <TextField
              label="Notes"
              multiline
              rows={4}
              fullWidth
              value={formData.note || ''}
              onChange={e => handleChange('note', e.target.value)}
              sx={{ mt: 2 }}
            />
          )}
        </div>
        <div className="journal-toggles">
          {[
            { key: 'is_escalated', label: 'ESCALATE' },
            { key: 'is_pricing', label: 'PRICE ISSUE' },
            { key: 'is_placeholder', label: 'PLACEHOLDER' },
            { key: 'is_no_error', label: 'NO ERROR' },
            { key: 'is_local_leadership', label: 'LOCAL LEADERSHIP' },
          ].map(({ key, label }) => (
            <button
              key={key}
              type="button"
              className={`journal-toggle ${formData[key] ? 'active' : ''}`}
              onClick={() =>
                setFormData(prev => ({ ...prev, [key]: !prev[key] }))
              }
            >
              {label}
            </button>
          ))}
        </div>
      </DialogContent>

      <DialogActions className="journal-modal-footer">
        <Button onClick={onClose} disabled={submitting}>
          Cancel
        </Button>

        <Button
          onClick={handleSubmit}
          disabled={submitting}
          startIcon={
            submitting ? (
              <CircularProgress size={16} thickness={5} />
            ) : null
          }
        >
          {submitting ? 'Saving…' : 'Save Changes'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default JournalEditModal;